import {
  FeedbackErrorResponse,
  FeedbackRequest,
  FeedbackSubmissionError,
  FeedbackSuccessResponse,
} from './feedback.types';
import { apiUrl } from 'src/constants/config';

const FEEDBACK_API_PATH = 'tools/feedback';
const apiBaseUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;

export const FEEDBACK_API_URL = `${apiBaseUrl}${FEEDBACK_API_PATH}`;

function parseFieldErrors(body: FeedbackErrorResponse): FeedbackSubmissionError['fieldErrors'] | undefined {
  if (!body || typeof body !== 'object' || 'error' in body) {
    return undefined;
  }

  const fieldErrors: NonNullable<FeedbackSubmissionError['fieldErrors']> = {};

  (['type', 'title', 'description', 'pageContext'] as const).forEach((field) => {
    const messages = body[field];
    if (Array.isArray(messages) && messages.length > 0) {
      fieldErrors[field] = messages[0];
    }
  });

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

function parseNonFieldError(body: FeedbackErrorResponse): string | undefined {
  if (!body || typeof body !== 'object' || 'error' in body) {
    return undefined;
  }

  const messages = body.non_field_errors;
  if (Array.isArray(messages) && messages.length > 0) {
    return messages.join(' ');
  }

  return undefined;
}

function parseErrorMessage(body: FeedbackErrorResponse, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
    return body.error;
  }

  const nonFieldError = parseNonFieldError(body);
  if (nonFieldError) {
    return nonFieldError;
  }

  const fieldErrors = parseFieldErrors(body);
  if (fieldErrors) {
    // Field-level errors should be shown inline, not as the primary form error.
    return 'Please correct the highlighted fields.';
  }

  return fallback;
}

async function parseErrorResponse(response: Response): Promise<FeedbackSubmissionError> {
  let body: FeedbackErrorResponse | undefined;

  try {
    body = await response.json() as FeedbackErrorResponse;
  } catch {
    body = undefined;
  }

  const fieldErrors = body ? parseFieldErrors(body) : undefined;
  const fallbackMessage = response.status === 502
    ? 'Couldn’t submit feedback right now. Please try again.'
    : 'Failed to submit feedback. Please try again.';

  return {
    status: response.status,
    message: body ? parseErrorMessage(body, fallbackMessage) : fallbackMessage,
    fieldErrors,
    isRetryable: response.status === 502,
  };
}

export async function submitFeedbackRequest(
  request: FeedbackRequest,
  accessToken?: string | null,
): Promise<FeedbackSuccessResponse> {
  const headers: HeadersInit = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(FEEDBACK_API_URL, {
    method: 'POST',
    headers,
    body: buildMultipartBody(request, request.pageContext),
  });

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  return await response.json() as FeedbackSuccessResponse;
}

function buildMultipartBody(request: FeedbackRequest, pageContext?: FeedbackRequest['pageContext']): FormData {
  const formData = new FormData();

  formData.append('type', request.type);
  formData.append('title', request.title);
  formData.append('description', request.description);

  if (pageContext) {
    formData.append('pageContext', JSON.stringify(pageContext));
  }

  if (request.screenshot) {
    formData.append('screenshot', request.screenshot);
  }

  return formData;
}
